import {
    Box,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    Typography,
} from "@mui/material";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useAppContext } from "../../../shared/contexts/app-context/components/AppContext";
import {
    useOrders,
    usePortfolioManagement,
    usePortfolioProfile,
    usePortfolioSelection,
} from "../../../shared/contexts/portfolio-context/components/PortfolioContextProvider";
import { PortfolioGroupedItem } from "../models/PortfolioGroupedItem";
import { OrderModel } from "../../portfolio-info/types/OrderModel";
import { assetService } from "../../../shared/services/AssetService";
import { blackScholesModelCalculationService } from "../../../shared/services/BlackScholesModelCalculationService";
import { dateTimeConversionService } from "../../../shared/services/DateTimeConversionService";
import { ContextMenu, PositionsToolsPanel } from "../controls";
import {
    ApplicationTooltip,
    LineDelimiter,
    OptionExpirationTableCell,
    PortfolioBalanceTooltipContent,
    PositionInfoTooltipTitle,
    TableFooterRow,
} from "../views";
import { ContextMenuItem } from "../controls/context-menu/ContextMenuItem";
import { orderMappers } from "../../portfolio-info/mappers/OrderMappers";
import { TableBodyRow, TableHeaderRow } from "../../shared/views";
import {
    POSITIONS_TABLE_ROW_PADDING_Y,
    TABLE_PARAMETER_COLUMN_MIN_WIDTH,
    TABLE_SYMBOL_EXP_CELL_FONT_SIZE
} from "../../shared/constants/table.constants";
import { NumberFormatOptions, numberFormatters } from "../../shared/utils/numberFormatters";
import { OrderState } from "../../../shared/types/OrderState";
import { AssetType } from "../../../shared/types/AssetType";
import {
    PositionsValuationContext,
    positionsProcessor,
} from "../utils/PositionsProcessor";
import {
    PositionImpliedVolatilityChangingParameters
} from "../controls/position-implied-volatility-selector/PositionImpliedVolatilityChangingParameters";
import { PositionManagerDialog } from "../dialogs/position-manager-dialog/PositionManagerDialog";
import { customImpliedVolatilityInfoBuilder } from "../utils/CustomImpliedVolatilityInfoBuilder";
import EditIcon from '@mui/icons-material/Edit';
import { portfolioPerformanceService } from "../../../shared/services/PortfolioPerformanceService";
import { colourChartStyle } from "../../portfolio-profile/components/charts/styles/ColourChartStyle";
import { mathCalculationService } from "../../../shared/services/MathCalculationService";
import { getTargetPlatformName, getTargetPlatformText, } from "../utils/TargetPlatformUtils";
import type { PositionCustomImpliedVolatilityUpdate } from "../../../shared/services/orders-service/OrdersService";
import { TargetPlatform } from "../../../shared/types/TargetPlatform";
import { PositionStateFilter } from "../../../shared/types/PositionStateFilter";
import { PositionFilters, positionFilterUtils } from "../utils/PositionFilterUtils";
import {
    portfolioBalanceCalculationService,
} from "../../../shared/services/portfolio-balance-calculation-service/PortfolioBalanceCalculationService";

type PositionsProps = {
    isExpanded: boolean;
    setIsExpanded: Dispatch<SetStateAction<boolean>>;
};

export function Positions({
    isExpanded,
    setIsExpanded,
}: PositionsProps) {
    const {
        marketDataDto,
        selectedPortfolioBalance,
        setSelectedPortfolioBalance,
        selectedPortfolioVolume,
        setSelectedPortfolioVolume,
    } = useAppContext();
    
    const {
        selectedPortfolio,
        isAllPortfoliosSelected,
        underlyingAssetPortfolios,
    } = usePortfolioSelection();
    const {
        saveSelectedPortfolio,
    } = usePortfolioManagement();
    const {
        updateSelectedPortfolio,
    } = usePortfolioProfile();
    const {
        addFuturesToPortfolio,
        updatePositionsInSelectedPortfolio,
        saveCustomImpliedVolatility,
    } = useOrders();
    
    const [selectedPositionStateFilter, setSelectedPositionStateFilter] =
        useState<PositionStateFilter>(PositionStateFilter.Open);
    const [selectedAssetTypeFilter, setSelectedAssetTypeFilter] = useState<AssetType | null>(null);
    const [selectedTargetPlatformFilter, setSelectedTargetPlatformFilter] =
        useState<TargetPlatform | null>(null);
    const [filtersSourcePortfolio, setFiltersSourcePortfolio] = useState(selectedPortfolio);
    const [mergePositionsAcrossPlatforms, setMergePositionsAcrossPlatforms] = useState<boolean>(false);
    const [selectedPosition, setSelectedPosition] = useState<PortfolioGroupedItem | null>(null);
    const [isSetCustomImpliedVolatilityDialogOpen, setIsSetCustomImpliedVolatilityDialogOpen] = useState<boolean>(false);
    const [valuationDate] = useState<Date>(() => new Date());

    const portfolioSettings = selectedPortfolio?.portfolioSettings;
    const underlyingPrice = portfolioSettings?.useCustomPrice === true
        ? portfolioSettings.customPrice
        : marketDataDto?.UnderlyingPrice ?? marketDataDto?.BaseAsset.Price ?? 0;
    const valuationContext: PositionsValuationContext | null =
        portfolioSettings?.useCustomPrice === true ||
        portfolioSettings?.isCustomImpliedVolatilityEnabled === true
            ? {
                underlyingPrice,
                valuationDate,
                useCustomImpliedVolatility:
                    portfolioSettings?.isCustomImpliedVolatilityEnabled === true,
            }
            : null;
    const positions = selectedPortfolio !== null
        ? positionsProcessor.getSummarizedOrders(
            selectedPortfolio,
            false,
            null,
            valuationContext,
        )
        : [];
    const filterSourcePositions = selectedPortfolio !== null
        ? positionsProcessor.getSummarizedOrders(
            selectedPortfolio,
            mergePositionsAcrossPlatforms,
            selectedTargetPlatformFilter,
            valuationContext,
        )
        : [];
    const selectedFilters: PositionFilters = {
        positionState: selectedPositionStateFilter,
        assetType: selectedAssetTypeFilter,
        targetPlatform: selectedTargetPlatformFilter,
    };
    const filterAvailability = positionFilterUtils.getAvailability(
        filterSourcePositions,
        positions,
        selectedFilters,
    );
    const filteredPositions = positionFilterUtils.apply(filterSourcePositions, selectedFilters);

    const formatOptions: NumberFormatOptions = {
        useGroupFormat: false,
        alwaysUseSign: true
    };
    
    const getRelatedPortfolioInfo = (position: PortfolioGroupedItem): string[] =>
        isAllPortfoliosSelected
            ? position.portfolioAllocations
                .map(allocation => {
                    const portfolioName = underlyingAssetPortfolios
                        .find(portfolio => portfolio.id === allocation.portfolioId)?.name;

                    return portfolioName !== undefined
                        ? `${numberFormatters.format4(allocation.currentSize, formatOptions)} · ${portfolioName}`
                        : null;
                })
                .filter((portfolioInfo): portfolioInfo is string => portfolioInfo !== null)
            : [];
    const positionFilterSignature = positions
        .map(position => `${position.type}:${position.targetPlatform}:${positionFilterUtils.isOpen(position)}`)
        .sort()
        .join('|');

    useEffect(() => {
        if (selectedPortfolio === null || selectedPortfolio.portfolioSettings === null) {
            setSelectedPositionStateFilter(PositionStateFilter.Open);
            setSelectedAssetTypeFilter(null);
            setSelectedTargetPlatformFilter(null);
            setMergePositionsAcrossPlatforms(false);
            setFiltersSourcePortfolio(selectedPortfolio);
            return;
        }

        setSelectedPositionStateFilter(selectedPortfolio.portfolioSettings.positionStateFilter);
        setSelectedAssetTypeFilter(selectedPortfolio.portfolioSettings.positionsAssetTypeFilter);
        setSelectedTargetPlatformFilter(
            selectedPortfolio.portfolioSettings.positionsTargetPlatformFilter,
        );
        setMergePositionsAcrossPlatforms(
            selectedPortfolio.portfolioSettings.mergePositionsAcrossPlatforms,
        );
        setFiltersSourcePortfolio(selectedPortfolio);
    }, [selectedPortfolio]);

    useEffect(() => {
        if (
            selectedPortfolio === null ||
            selectedPortfolio.portfolioSettings === null ||
            filtersSourcePortfolio !== selectedPortfolio
        ) {
            return;
        }

        const normalizedFilters = positionFilterUtils.normalize(selectedFilters, filterAvailability);
        if (positionFilterUtils.areEqual(selectedFilters, normalizedFilters)) {
            return;
        }

        setSelectedPositionStateFilter(normalizedFilters.positionState);
        setSelectedAssetTypeFilter(normalizedFilters.assetType);
        setSelectedTargetPlatformFilter(normalizedFilters.targetPlatform);
        setSelectedPosition(null);

        selectedPortfolio.portfolioSettings.positionStateFilter = normalizedFilters.positionState;
        selectedPortfolio.portfolioSettings.positionsAssetTypeFilter = normalizedFilters.assetType;
        selectedPortfolio.portfolioSettings.positionsTargetPlatformFilter = normalizedFilters.targetPlatform;

        void (async (): Promise<void> => {
            await updateSelectedPortfolio();
            await saveSelectedPortfolio();
        })();
    }, [
        selectedPortfolio,
        filtersSourcePortfolio,
        selectedPositionStateFilter,
        selectedAssetTypeFilter,
        selectedTargetPlatformFilter,
        mergePositionsAcrossPlatforms,
        positionFilterSignature,
    ]);

    const handlePositionStateFilterChange = (value: SetStateAction<PositionStateFilter>): void => {
        const nextPositionStateFilter = typeof value === 'function'
            ? value(selectedPositionStateFilter)
            : value;

        setSelectedPositionStateFilter(nextPositionStateFilter);
        setSelectedPosition(null);

        if (selectedPortfolio === null || selectedPortfolio.portfolioSettings === null) {
            return;
        }

        selectedPortfolio.portfolioSettings.positionStateFilter = nextPositionStateFilter;

        void (async (): Promise<void> => {
            await updateSelectedPortfolio();
            await saveSelectedPortfolio();
        })();
    };

    const handleAssetTypeFilterChange = (value: SetStateAction<AssetType | null>): void => {
        const nextAssetTypeFilter = typeof value === 'function'
            ? value(selectedAssetTypeFilter)
            : value;

        setSelectedAssetTypeFilter(nextAssetTypeFilter);
        setSelectedPosition(null);

        if (selectedPortfolio === null || selectedPortfolio.portfolioSettings === null) {
            return;
        }

        selectedPortfolio.portfolioSettings.positionsAssetTypeFilter = nextAssetTypeFilter;

        void (async (): Promise<void> => {
            await updateSelectedPortfolio();
            await saveSelectedPortfolio();
        })();
    };

    const handleTargetPlatformFilterChange = (value: SetStateAction<TargetPlatform | null>): void => {
        const nextTargetPlatformFilter = typeof value === 'function'
            ? value(selectedTargetPlatformFilter)
            : value;

        setSelectedTargetPlatformFilter(nextTargetPlatformFilter);
        setSelectedPosition(null);

        if (selectedPortfolio === null || selectedPortfolio.portfolioSettings === null) {
            return;
        }

        selectedPortfolio.portfolioSettings.positionsTargetPlatformFilter = nextTargetPlatformFilter;

        void (async (): Promise<void> => {
            await updateSelectedPortfolio();
            await saveSelectedPortfolio();
        })();
    };

    const handleResetFilters = (): void => {
        setSelectedPositionStateFilter(PositionStateFilter.All);
        setSelectedAssetTypeFilter(null);
        setSelectedTargetPlatformFilter(null);
        setMergePositionsAcrossPlatforms(false);
        setSelectedPosition(null);

        if (selectedPortfolio === null || selectedPortfolio.portfolioSettings === null) {
            return;
        }

        selectedPortfolio.portfolioSettings.positionStateFilter = PositionStateFilter.All;
        selectedPortfolio.portfolioSettings.positionsAssetTypeFilter = null;
        selectedPortfolio.portfolioSettings.positionsTargetPlatformFilter = null;
        selectedPortfolio.portfolioSettings.mergePositionsAcrossPlatforms = false;

        void (async (): Promise<void> => {
            await updateSelectedPortfolio();
            await saveSelectedPortfolio();
        })();
    };

    const handleIncludeFeesInBalanceChange = (value: SetStateAction<boolean>): void => {
        if (!selectedPortfolio?.portfolioSettings) {
            return;
        }

        const settings = selectedPortfolio.portfolioSettings;
        settings.includeFeesInBalance = typeof value === 'function'
            ? value(settings.includeFeesInBalance ?? false)
            : value;

        void (async (): Promise<void> => {
            await updateSelectedPortfolio();
            await saveSelectedPortfolio();
        })();
    };

    const handleMergePositionsAcrossPlatformsChange = (value: SetStateAction<boolean>): void => {
        const nextMergePositionsAcrossPlatforms = typeof value === 'function'
            ? value(mergePositionsAcrossPlatforms)
            : value;

        setMergePositionsAcrossPlatforms(nextMergePositionsAcrossPlatforms);
        setSelectedPosition(null);

        if (selectedPortfolio === null || selectedPortfolio.portfolioSettings === null) {
            return;
        }

        selectedPortfolio.portfolioSettings.mergePositionsAcrossPlatforms =
            nextMergePositionsAcrossPlatforms;

        void (async (): Promise<void> => {
            await updateSelectedPortfolio();
            await saveSelectedPortfolio();
        })();
    };

    const hasOpenPlatformAllocation = (position: PortfolioGroupedItem): boolean =>
        position.platformAllocations.some(allocation =>
            mathCalculationService.round(allocation.currentSize, 6) !== 0);

    const handleOnPositionClick = (position: PortfolioGroupedItem) => {
        setSelectedPosition(position);
    };

    const isCustomImpliedVolatilityApplicable = (position: PortfolioGroupedItem | null): boolean => {
        return (
            position !== null &&
            assetService.isGroupedOptions(position) &&
            hasOpenPlatformAllocation(position) &&
            !assetService.isGroupedOptionsExpired(position)
        );
    };

    const handleEditPosition = (position: PortfolioGroupedItem) => {
        if (isAllPortfoliosSelected) {
            return;
        }

        setSelectedPosition(position);
        setIsSetCustomImpliedVolatilityDialogOpen(true);
    };
    
    const handleAddClosingOrder = async (
        position: PortfolioGroupedItem,
        closeContextMenu: () => void
    ) => {
        closeContextMenu();

        if (isAllPortfoliosSelected) {
            return;
        }
        
        if (
            marketDataDto === undefined ||
            selectedPortfolio === null || selectedPortfolio.portfolioSettings === null
        ) {
            return;
        }

        const portfolioSettings = selectedPortfolio!.portfolioSettings!;
        const underlyingPrice = portfolioSettings.useCustomPrice
            ? portfolioSettings.customPrice
            : marketDataDto.BaseAsset.Price ?? 0;

        const getOpenPrice = () => {
            if (assetService.isGroupedOptions(position)) {
                const timeToExpirationInShareOfYear = blackScholesModelCalculationService
                    .getTimeToExpirationInShareOfYear(position.expireAt!);

                return blackScholesModelCalculationService.getPremium(
                    position.type, underlyingPrice, position.strike!,
                    timeToExpirationInShareOfYear, position.impliedVolatility!);
            } else {
                return underlyingPrice;
            }
        }

        const openPrice = getOpenPrice();

        const newOrderModels = position.platformAllocations
            .filter(allocation => mathCalculationService.round(allocation.currentSize, 6) !== 0)
            .map(allocation => new OrderModel({
                externalId: 0,
                exchangeOrderId: null,
                internalId: crypto.randomUUID(),
                state: OrderState.Active,
                targetPlatform: allocation.targetPlatform,
                openedPositionAt: dateTimeConversionService.getCurrentUtcDate(),
                expireAt: position.expireAt,
                impliedVolatilityAtOpen: position.impliedVolatility,
                customImpliedVolatility: position.impliedVolatility,
                symbol: position.symbol,
                size: -allocation.currentSize,
                openPrice: openPrice,
                markPrice: openPrice,
                markImpliedVolatility: position.impliedVolatility,
                type: position.type,
                strike: position.strike,
                underlyingPriceAtOpen: underlyingPrice,
                delta: 0,
                gamma: null,
                vega: null,
                theta: null,
                comment: null,
                expirationPrice: position.expirationPrice,
                dateForPricing: dateTimeConversionService.getCurrentUtcDate(),
                impliedVolatilityForPricing: position.impliedVolatility,
                underlyingPriceForPricing: underlyingPrice,
            }));

        await addFuturesToPortfolio(newOrderModels);
    };

    const portfolioBalance = selectedPortfolio !== null
        ? portfolioBalanceCalculationService.calculateAtDate(
            selectedPortfolio,
            valuationContext,
            portfolioSettings?.includeFeesInBalance ?? false,
        )
        : 0;
    const performanceContext = selectedPortfolio !== null
        ? portfolioPerformanceService.createContext(positions, selectedPortfolio)
        : null;
    const portfolioVolume = performanceContext?.investedCapital ?? 0;

    setSelectedPortfolioBalance(portfolioBalance);
    setSelectedPortfolioVolume(portfolioVolume);

    const { roi, annualisedRoi } = performanceContext !== null
        ? portfolioPerformanceService.calculate(portfolioBalance, performanceContext)
        : { roi: null, annualisedRoi: null };
    const isCustomImpliedVolatilityEnabled =
        selectedPortfolio?.portfolioSettings?.isCustomImpliedVolatilityEnabled ?? false;
    const activeImpliedVolatilityTextColour = "rgba(0,0,0,1)";
    const inactiveImpliedVolatilityTextColour = "rgba(0,0,0,0.4)";
    const customImpliedVolatilityTextColour = isCustomImpliedVolatilityEnabled
        ? colourChartStyle.selectedCustomLineColour
        : inactiveImpliedVolatilityTextColour;
    const isCustomPriceEnabled = selectedPortfolio?.portfolioSettings?.useCustomPrice ?? false;
    const customUnderlyingPriceTextColour = isCustomPriceEnabled
        ? colourChartStyle.selectedCustomLineColour
        : undefined;
    
    const updatePositionsInSelectedPortfolioWrapper = async (
        optionSymbol: string,
        updates: PositionCustomImpliedVolatilityUpdate[],
    ): Promise<void> => {
        if (isAllPortfoliosSelected) {
            return;
        }

        await updatePositionsInSelectedPortfolio(optionSymbol, updates);
    }

    const handleSetCustomImpliedVolatility = async (customImpliedVolatility: number): Promise<void> => {
        if (selectedPosition === null || selectedPortfolio === null || isAllPortfoliosSelected) {
            return;
        }

        const updates = selectedPosition.platformAllocations.map(allocation => ({
            targetPlatform: allocation.targetPlatform,
            customImpliedVolatility,
        }));

        await updatePositionsInSelectedPortfolio(selectedPosition.symbol, updates);

        const updatesForPersistence = selectedPosition.platformAllocations
            .map(allocation => customImpliedVolatilityInfoBuilder.build(
                selectedPortfolio,
                selectedPosition.symbol,
                allocation.targetPlatform,
            ));
        const positionsForUpdate = updatesForPersistence
            .flatMap(update => update.positionsForUpdate);
        const ordersForUpdate = updatesForPersistence
            .flatMap(update => update.ordersForUpdate);

        positionsForUpdate.forEach(position => {
            position.customImpliedVolatility = customImpliedVolatility;
        });
        ordersForUpdate.forEach(order => {
            order.customImpliedVolatility = customImpliedVolatility;
        });

        await saveCustomImpliedVolatility(positionsForUpdate, ordersForUpdate);
    };
    
    const buildPositionImpliedVolatilityChangingParameters = (): PositionImpliedVolatilityChangingParameters => {
        return {
            selectedPosition: selectedPosition,
            positions: positions,
            updatePositionsInSelectedPortfolioWrapper: updatePositionsInSelectedPortfolioWrapper,
        };
    }
    
    return (
        <Stack
            sx={{
                flex: 1,
                minHeight: 0,
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 0,
                paddingBottom: '8px',
            }}
        >
            <PositionManagerDialog
                isOpen={isSetCustomImpliedVolatilityDialogOpen}
                setIsOpen={setIsSetCustomImpliedVolatilityDialogOpen}
                position={selectedPosition}
                submitAction={handleSetCustomImpliedVolatility}
            />
            <PositionsToolsPanel
                selectedPositionStateFilter={selectedPositionStateFilter}
                setSelectedPositionStateFilter={handlePositionStateFilterChange}
                selectedAssetTypeFilter={selectedAssetTypeFilter}
                setSelectedAssetTypeFilter={handleAssetTypeFilterChange}
                selectedTargetPlatformFilter={selectedTargetPlatformFilter}
                setSelectedTargetPlatformFilter={handleTargetPlatformFilterChange}
                filterAvailability={filterAvailability}
                resetFilters={handleResetFilters}
                includeFeesInBalance={portfolioSettings?.includeFeesInBalance ?? false}
                setIncludeFeesInBalance={handleIncludeFeesInBalanceChange}
                mergePositionsAcrossPlatforms={mergePositionsAcrossPlatforms}
                setMergePositionsAcrossPlatforms={handleMergePositionsAcrossPlatformsChange}
                isExpanded={isExpanded}
                setIsExpanded={setIsExpanded}
                positionImpliedVolatilityChangingParameters={buildPositionImpliedVolatilityChangingParameters()}
            />
            <LineDelimiter/>
            <Paper
                sx={{
                    flex: '1',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    borderRadius: 0,
                }}
            >
                <TableContainer
                    sx={{
                        flex: '1',
                        padding: '0px',
                        margin: '0px'
                    }}
                >
                    <Table
                        size="small"
                        stickyHeader
                        sx={{padding: '0px', margin: '0px', verticalAlign: 'text-top'}}
                    >
                        <TableHead>
                            <TableHeaderRow>
                                <ApplicationTooltip
                                    title="Target platform: B = Binance, D = Deribit, S = Samsung Wallet, T = Trust Wallet, R = RevolutX, * = merged across platforms"
                                    disableInteractive
                                    offset={[0, -7]}
                                >
                                    <TableCell>T</TableCell>
                                </ApplicationTooltip>
                                <ApplicationTooltip
                                    title="Edit position"
                                    disableInteractive
                                    offset={[0, -7]}
                                >
                                    <TableCell>E</TableCell>
                                </ApplicationTooltip>
                                <TableCell sx={{"&&":{textAlign: 'left'}}}>Symbol</TableCell>
                                <TableCell>Exp</TableCell>
                                <TableCell>Size</TableCell>
                                <ApplicationTooltip
                                    title="Average open price"
                                    disableInteractive
                                    offset={[0, -7]}
                                >
                                    <TableCell>Open</TableCell>
                                </ApplicationTooltip>
                                <ApplicationTooltip
                                    maxWidth="300px"
                                    title={
                                        <>
                                            <Box marginBottom={1}>
                                                Total invested money is calculated as the amount of capital required to open and maintain all positions.
                                            </Box>
                                            <Box marginBottom={0.5}>
                                                <b>Spot and options assets:</b> the actual amount paid to purchase the assets.
                                            </Box>
                                            <Box>
                                                <b>Futures assets:</b> the amount of capital required to maintain the position, assuming the price moves against it by the configured loss threshold.
                                            </Box>
                                        </>
                                    }
                                    disableInteractive
                                    offset={[0, -7]}
                                >
                                    <TableCell>Volume</TableCell>
                                </ApplicationTooltip>
                                <ApplicationTooltip
                                    title="Current market price"
                                    disableInteractive
                                    offset={[0, -7]}
                                >
                                    <TableCell>Close</TableCell>
                                </ApplicationTooltip>
                                <TableCell>Fee</TableCell>
                                <ApplicationTooltip
                                    title={
                                        <PortfolioBalanceTooltipContent
                                            roi={roi}
                                            annualisedRoi={annualisedRoi}
                                        />
                                    }
                                    disableInteractive
                                    offset={[0, -7]}
                                >
                                    <TableCell>Balance</TableCell>
                                </ApplicationTooltip>
                                <ApplicationTooltip
                                    title="Current implied volatility"
                                    disableInteractive
                                    offset={[0, -7]}
                                >
                                    <TableCell
                                        sx={{
                                            color: isCustomImpliedVolatilityEnabled
                                                ? inactiveImpliedVolatilityTextColour
                                                : activeImpliedVolatilityTextColour
                                        }}
                                    >
                                        IV,%
                                    </TableCell>
                                </ApplicationTooltip>
                                <ApplicationTooltip
                                    title="Custom implied volatility"
                                    disableInteractive
                                    offset={[0, -7]}
                                >
                                    <TableCell
                                        sx={{
                                            color: customImpliedVolatilityTextColour
                                        }}
                                    >
                                        CIV,%
                                    </TableCell>
                                </ApplicationTooltip>
                                <ApplicationTooltip title="Delta" disableInteractive offset={[0, -7]}>
                                    <TableCell sx={{minWidth: TABLE_PARAMETER_COLUMN_MIN_WIDTH}}>D</TableCell>
                                </ApplicationTooltip>
                                <ApplicationTooltip
                                    title="Gamma is displayed in millionths (10⁻⁶)"
                                    disableInteractive
                                    offset={[0, -7]}
                                >
                                    <TableCell sx={{minWidth: TABLE_PARAMETER_COLUMN_MIN_WIDTH}}>G</TableCell>
                                </ApplicationTooltip>
                                <ApplicationTooltip title="Vega" disableInteractive offset={[0, -7]}>
                                    <TableCell sx={{minWidth: TABLE_PARAMETER_COLUMN_MIN_WIDTH}}>V</TableCell>
                                </ApplicationTooltip>
                                <ApplicationTooltip title="Theta" disableInteractive offset={[0, -7]}>
                                    <TableCell sx={{minWidth: TABLE_PARAMETER_COLUMN_MIN_WIDTH}}>T</TableCell>
                                </ApplicationTooltip>
                            </TableHeaderRow>
                        </TableHead>
                        <TableBody>
                            {filteredPositions
                                .map((position) => (
                                    <ContextMenu<PortfolioGroupedItem>
                                        key={`${position.type}:${position.symbol}:${position.platformAllocations
                                            .map(allocation => allocation.targetPlatform)
                                            .join(',')}`}
                                        renderItems={(position, closeContextMenu) => (
                                            <>
                                                <ContextMenuItem
                                                    onClick={() => handleAddClosingOrder(position, closeContextMenu)}
                                                    disabled={isAllPortfoliosSelected || !hasOpenPlatformAllocation(position)}
                                                >
                                                    Add closing order
                                                </ContextMenuItem>
                                                <ContextMenuItem
                                                    onClick={() => {
                                                        closeContextMenu();
                                                        handleEditPosition(position);
                                                    }}
                                                    disabled={isAllPortfoliosSelected || !isCustomImpliedVolatilityApplicable(position)}
                                                >
                                                    Set custom IV
                                                </ContextMenuItem>
                                            </>
                                        )}
                                    >
                                        {({onContextMenu}) => (
                                            <TableBodyRow
                                                onClick={() => handleOnPositionClick(position)}
                                                onContextMenu={
                                                    (event: React.MouseEvent) => {
                                                        setSelectedPosition(position);
                                                        onContextMenu(event, position)
                                                    }
                                                }
                                                paddingY={POSITIONS_TABLE_ROW_PADDING_Y}
                                                style={{
                                                    ...(!hasOpenPlatformAllocation(position) ||
                                                        (assetService.isOptionType(position.type) &&
                                                        assetService.isOptionPositionExpired(position))
                                                            ? {backgroundColor: 'rgba(236,255,229,0.67)'}
                                                            : {}),
                                                    ...(selectedPosition &&
                                                        selectedPosition.type === position.type &&
                                                        selectedPosition.symbol === position.symbol &&
                                                        selectedPosition.platformAllocations
                                                            .map(allocation => allocation.targetPlatform)
                                                            .join(',') === position.platformAllocations
                                                            .map(allocation => allocation.targetPlatform)
                                                            .join(',')
                                                                ? {backgroundColor: 'rgba(255,224,122,0.5)'}
                                                                : {}),
                                                }}
                                            >
                                                <ApplicationTooltip
                                                    title={position.platformAllocations.length > 1
                                                        ? position.platformAllocations.map(allocation => (
                                                            <Box key={allocation.targetPlatform}>
                                                                {getTargetPlatformName(allocation.targetPlatform)}:{' '}
                                                                {numberFormatters.format4(allocation.currentSize)}
                                                            </Box>
                                                        ))
                                                        : ''}
                                                    disableInteractive
                                                    offset={[0, -7]}
                                                >
                                                    <TableCell
                                                        align="center"
                                                        sx={{
                                                            "&&": {
                                                                paddingX: '0px',
                                                                minWidth: '25px'
                                                            },
                                                            fontWeight: 'bold',
                                                        }}
                                                    >
                                                        {position.platformAllocations.length > 1
                                                            ? '···'
                                                            : getTargetPlatformText(position.targetPlatform)}
                                                    </TableCell>
                                                </ApplicationTooltip>
                                                <TableCell
                                                    align="center"
                                                    sx={{
                                                        "&&": {
                                                            paddingX: "2px",
                                                        },
                                                    }}
                                                >
                                                        <IconButton
                                                            color="warning"
                                                            size="small"
                                                            sx={{padding: '0px'}}
                                                            disabled={isAllPortfoliosSelected || !isCustomImpliedVolatilityApplicable(position)}
                                                            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                                                                event.stopPropagation();
                                                                handleEditPosition(position);
                                                            }}
                                                        >
                                                            <EditIcon/>
                                                        </IconButton>
                                                </TableCell>
                                                <ApplicationTooltip
                                                    title={
                                                        <PositionInfoTooltipTitle
                                                            portfolioInfo={getRelatedPortfolioInfo(position)}
                                                        />
                                                    }
                                                    disableInteractive
                                                    offset={[0, -7]}
                                                    maxWidth={500}
                                                >
                                                    <TableCell align="left">
                                                        <Typography
                                                            noWrap
                                                            sx={{
                                                                fontSize: TABLE_SYMBOL_EXP_CELL_FONT_SIZE,
                                                                textOverflow: 'ellipsis',
                                                            }}
                                                        >
                                                            {orderMappers.getSymbolName(position.symbol, position.type)}
                                                        </Typography>
                                                    </TableCell>
                                                </ApplicationTooltip>
                                                <OptionExpirationTableCell
                                                    isOption={assetService.isGroupedOptions(position)}
                                                    expireAt={position.expireAt}
                                                    expirationPrice={position.expirationPrice}
                                                />
                                                <TableCell align="right">
                                                    {numberFormatters.format4(position.currentSize)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {numberFormatters.format0(position.averageOpenPrice)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {numberFormatters.format0(position.volume)}
                                                </TableCell>
                                                <TableCell
                                                    align="right"
                                                    sx={{
                                                        color: position.type === AssetType.Futures
                                                            ? customUnderlyingPriceTextColour
                                                            : undefined,
                                                    }}
                                                >
                                                    {
                                                        (assetService.isGroupedOptions(position) &&
                                                        assetService.isGroupedOptionsExpired(position)) ||
                                                        !hasOpenPlatformAllocation(position)
                                                            ? null
                                                            : numberFormatters.format0(position.markPrice)
                                                    }
                                                </TableCell>
                                                <TableCell align="right">{position.fee.toFixed(2)}</TableCell>
                                                <TableCell align="right">
                                                    {numberFormatters.format0(position.balance)}
                                                </TableCell>
                                                <TableCell
                                                    align="right"
                                                    sx={{
                                                        color: isCustomImpliedVolatilityEnabled
                                                            ? inactiveImpliedVolatilityTextColour
                                                            : activeImpliedVolatilityTextColour
                                                    }}
                                                >
                                                    {
                                                        assetService.isGroupedOptions(position) &&
                                                        !assetService.isGroupedOptionsExpired(position) &&
                                                        hasOpenPlatformAllocation(position)
                                                            ? position.impliedVolatility !== null
                                                                ? numberFormatters.format2(position.impliedVolatility * 100)
                                                                : ''
                                                            : null
                                                    }
                                                </TableCell>
                                                <TableCell
                                                    align="right"
                                                    sx={{
                                                        color: customImpliedVolatilityTextColour
                                                    }}
                                                >
                                                    {
                                                        assetService.isGroupedOptions(position) &&
                                                        !assetService.isGroupedOptionsExpired(position) &&
                                                        hasOpenPlatformAllocation(position)
                                                            ? position.customImpliedVolatility !== null
                                                                ? numberFormatters.format2(position.customImpliedVolatility * 100)
                                                                : ''
                                                            : null
                                                    }
                                                </TableCell>
                                                <TableCell align="right">
                                                    {
                                                        hasOpenPlatformAllocation(position) &&
                                                        (!assetService.isOptionType(position.type) ||
                                                        !assetService.isGroupedOptionsExpired(position)                                                        )
                                                            ? numberFormatters.format3(position.delta)
                                                            : null
                                                    }
                                                </TableCell>
                                                <TableCell align="right">
                                                    {
                                                        position.gamma !== null &&
                                                        assetService.isGroupedOptions(position) &&
                                                        !assetService.isGroupedOptionsExpired(position) &&
                                                        hasOpenPlatformAllocation(position)
                                                            ? numberFormatters.format0(position.gamma * 10 ** 6)
                                                            : null
                                                    }
                                                </TableCell>
                                                <TableCell align="right">
                                                    {
                                                        position.vega !== null &&
                                                        assetService.isGroupedOptions(position) &&
                                                        !assetService.isGroupedOptionsExpired(position) &&
                                                        hasOpenPlatformAllocation(position)
                                                            ? numberFormatters.format0(position.vega)
                                                            : null
                                                    }
                                                </TableCell>
                                                <TableCell align="right">
                                                    {
                                                        position.theta !== null &&
                                                        assetService.isGroupedOptions(position) &&
                                                        !assetService.isGroupedOptionsExpired(position) &&
                                                        hasOpenPlatformAllocation(position)
                                                            ? numberFormatters.format0(position.theta)
                                                            : null
                                                    }
                                                </TableCell>
                                            </TableBodyRow>
                                        )}
                                    </ContextMenu>
                                ))}
                            <TableFooterRow>
                                <TableCell/>
                                <TableCell/>
                                <TableCell/>
                                <TableCell/>
                                <TableCell>
                                    {
                                        selectedAssetTypeFilter !== null
                                            ? numberFormatters.format4(filteredPositions
                                                .map(position => position.currentSize)
                                                .reduce((sum, value) => sum + value, 0))
                                            : null
                                    }
                                </TableCell>
                                <TableCell/>
                                <TableCell>
                                    {
                                        numberFormatters.format0(selectedPortfolioVolume)
                                    }
                                </TableCell>
                                <TableCell/>
                                <TableCell align="right">
                                    {
                                        numberFormatters.format2(
                                            selectedPortfolio
                                                ? portfolioBalanceCalculationService.calculateFees(selectedPortfolio)
                                                : 0
                                        )
                                    }
                                </TableCell>
                                <TableCell>
                                    {
                                        numberFormatters.format0(
                                            selectedPortfolioBalance -
                                            (selectedPortfolio && selectedPortfolio.portfolioSettings &&
                                            (selectedPortfolio.portfolioSettings.hideCommitedBalance ?? false) &&
                                            selectedPortfolio.portfolioSettings.commitedBalance
                                                ? selectedPortfolio.portfolioSettings?.commitedBalance
                                                : 0))
                                    }
                                </TableCell>
                                <TableCell/>
                                <TableCell/>
                                <TableCell>
                                    {numberFormatters.format3(positions
                                        .map(x => x.delta)
                                        .reduce((sum, value) => sum + value, 0))
                                    }
                                </TableCell>
                                <TableCell>
                                    {numberFormatters.format0(positions
                                        .map(x => x.gamma ?? 0)
                                        .reduce((sum, value) => sum + value, 0) * 10 ** 6)
                                    }
                                </TableCell>
                                <TableCell>
                                    {numberFormatters.format0(positions
                                        .map(x => x.vega ?? 0)
                                        .reduce((sum, value) => sum + value, 0))
                                    }
                                </TableCell>
                                <TableCell>
                                    {numberFormatters.format0(positions
                                        .map(x => x.theta ?? 0)
                                        .reduce((sum, value) => sum + value, 0))
                                    }
                                </TableCell>
                            </TableFooterRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Stack>
    );
}
